<script lang="ts">
	import { browser } from '$app/environment';
	import { goto } from '$app/navigation';
	import { base } from '$app/paths';
	import LazyImage from '$lib/LazyImage.svelte';
	import ObjectList from '$lib/ObjectList.svelte';
	import Scroller from '$lib/Scroller.svelte';
	import {
		filteredDates,
		filteredFrom,
		filteredPersons,
		filteredTask,
		filteredTo,
		filterIsVisible,
		query,
		searchParams,
	} from '$lib/shared/filter';
	import { labels } from '$lib/shared/labels';
	import { filterUser, taskFilterMap, tasks } from '$lib/shared/tasks';
	import { user } from '$lib/stores/auth';
	import { height } from '$lib/stores/header';
	import { siteTitle } from '$lib/stores/nav';
	import Tabs from '$lib/Tabs.svelte';
	import {
		MeiliSearch,
		type CategoriesDistribution,
		type Index,
		type Filter as MeiliFilter,
	} from 'meilisearch';
	import { onMount } from 'svelte';
	import Portal from 'svelte-portal/src/Portal.svelte';

	let index = 0;
	let tabs: Tab[] = [
		{ id: 'person', title: 'Person' },
		{ id: 'datum', title: 'Datum' },
		{ id: 'aufgabe', title: 'Aufgabe' },
	];
	let activeTab = tabs[0];
	let searched = false;

	let personTabs: Tab[] = [];
	let activePersonTab: Tab;
	const dateTabs: Tab[] = [
		{ id: 'datum', title: 'Datum' },
		{ id: 'zeitraum', title: 'Zeitraum' },
	];
	let activeDateTab = dateTabs[0];
	let leftBoundary: Date;
	let rightBoundary: Date;

	const reset = () => {
		$query = '';
		$filteredPersons = [];
		$filteredFrom = '';
		$filteredTo = '';
		$filteredDates = [];
		$filteredTask = '';
	};

	$: if (browser) {
		goto(`?${$searchParams.toString()}`, { keepFocus: true });
	}

	let filter: MeiliFilter;
	let dateRangeQuery: string;

	const fmt = Intl.DateTimeFormat('de-AT', {
		day: '2-digit',
		month: 'long',
		year: 'numeric',
	});

	$: if (activeDateTab.id === 'zeitraum') {
		dateRangeQuery = [
			$filteredFrom ? `posix >= ${new Date($filteredFrom).getTime() / 1000}` : null,
			$filteredTo ? `posix <= ${new Date($filteredTo).getTime() / 1000}` : null,
		]
			.filter(Boolean)
			.join(' AND ');
	} else {
		if ($filteredFrom) {
			dateRangeQuery = `posix = ${new Date($filteredFrom).getTime() / 1000}`;
		} else {
			dateRangeQuery = $filteredDates.map((date) => `posix = ${date}`).join(' OR ');
		}
	}

	const projectFilter = `project="${import.meta.env.VITE_PROJECT_NAME}"`;

	$: filter = [
		$filteredPersons.length
			? $filteredPersons.map((person) => `author = "${person}" OR recipient = "${person}"`)
			: null,
		$filteredTask ? taskFilterMap[$filteredTask] : null,
		$user && ['TRANSCRIBE', 'VERIFY'].includes($filteredTask)
			? filterUser($filteredTask, $user.id)
			: null,
		dateRangeQuery || null,
		projectFilter,
	].filter(Boolean);

	$: filtersAreSet =
		$filteredPersons.length +
		($filteredTask ? 1 : 0) +
		$filteredDates.length +
		(dateRangeQuery ? 1 : 0);

	const size = 60;
	let skip = 0;

	let objects = [];
	let total;
	let pending = false;
	let searchTimeout;
	let personOptions: CategoriesDistribution;
	let dateOptions: CategoriesDistribution;
	let randomSortIndex = Math.floor(Math.random() * 5);
	let ascDesc = Math.random() > 0.5 ? 'asc' : 'desc';

	const resetAndSearch = () => {
		skip = 0;
		triggerSearch(false);
	};

	/**
	 * Add a bit of delay between key strokes.
	 */
	const throttledSearch = () => {
		clearTimeout(searchTimeout);
		searchTimeout = setTimeout(resetAndSearch, 350);
	};

	$: {
		filter;
		resetAndSearch();
	}

	$: {
		$query;
		throttledSearch();
	}

	let searchIndex: Index;

	const getChar = (name: string) => {
		let char;
		if (name.match(/^\.\.\./)) {
			char = '...';
		} else {
			char = name.slice(0, 1);
		}
		return char;
	};

	const search = async (bootstrap = false) => {
		pending = true;
		if (!searchIndex) {
			const client = new MeiliSearch({
				host: `${document.location.origin}`,
				apiKey: import.meta.env.VITE_MEILI_API_KEY as string,
			});
			searchIndex = client.index(`objects`);
		}

		/**
		 * Replace `...`, `(...)` or `[...]` with `containsunreadableparts`
		 * @see https://gitlab.urbantrout.io/urbantrout/wienbibliothek-crowdsourcing/-/issues/62
		 **/
		const enhancedQuery = $query.replace(/[([]?\.{3}[)\]]?/g, 'containsunreadableparts');

		return searchIndex
			.search(bootstrap ? '' : enhancedQuery, {
				filter: bootstrap ? [projectFilter] : filter || [projectFilter],
				limit: size,
				offset: skip,
				facets: ['author', 'recipient', 'posix', 'status'],
				sort: !$query
					? [
							$filteredTask && $filteredTask === 'VERIFY'
								? 'latestApprovalCount:desc'
								: null,
							`randomSort.rnd${randomSortIndex}:${ascDesc}`,
					  ].filter(Boolean)
					: null, // Disable random sort if user searches by text input.
			})
			.then((result) => {
				searched = true;
				pending = false;
				total = result.estimatedTotalHits;

				const posixDates = Object.keys(result.facetDistribution.posix).map(
					(posix) => parseInt(posix, 10) * 1000
				);

				/**
				 * Update personOptions only if:
				 * - there are no options yet
				 * - another tab is active
				 * - no filter is set (click on reset button)
				 */

				if (!personOptions || activeTab.id !== 'person' || !filtersAreSet) {
					personOptions = Object.fromEntries(
						Object.entries({
							...result.facetDistribution.author,
							...result.facetDistribution.recipient,
						}).sort((a, b) => {
							if (a[0].match(/^\.\.\./)) {
								return 1;
							}
							if (b[0].match(/^\.\.\./)) {
								return -1;
							}
							return a[0].localeCompare(b[0]);
						})
					);
					personTabs = Object.keys(personOptions).reduce((acc, cur) => {
						const char = getChar(cur);
						const tab = { id: char, title: char };
						if (!acc.some((n) => n.id === tab.id)) {
							acc.push(tab);
						}
						return acc;
					}, []);

					activePersonTab = activePersonTab || personTabs[0];
				}
				if (activeTab.id !== 'datum' || !filtersAreSet) {
					if (posixDates.length) {
						leftBoundary = new Date(Math.min(...posixDates));
						rightBoundary = new Date(Math.max(...posixDates));
						dateOptions = result.facetDistribution.posix;
					}
				}

				if (!$filteredPersons.length) {
					Object.fromEntries(
						Object.entries({
							...result.facetDistribution.author,
							...result.facetDistribution.recipient,
						}).sort()
					);
				}

				return result.hits;
			});
	};

	const triggerSearch = async (append = true) => {
		if (pending || !browser) {
			return;
		}

		if (!append) {
			window.scrollTo({ top: 0, behavior: 'auto' });
			objects = [];
			randomSortIndex = Math.floor(Math.random() * 5);
			ascDesc = Math.random() > 0.5 ? 'asc' : 'desc';
		}

		const result = await search();

		if (!result) {
			return;
		}

		if (append) {
			objects = [...objects, ...result];
		} else {
			objects = result;
		}
	};

	onMount(async () => {
		$siteTitle = 'Briefe';

		resetAndSearch();
	});

	const handleKeydown = (event) => {
		if (event.key === 'Escape') {
			toggleFilter();
		}
	};

	const toggleFilter = () => {
		if (!activeTab) {
			activeTab = tabs[0];
		}
		$filterIsVisible = !$filterIsVisible;
	};

	$: if ($filterIsVisible) {
		if (browser) {
			document.documentElement.classList.add('overflow-hidden');
			window.addEventListener('keydown', handleKeydown);
		}
	} else {
		if (browser) {
			document.documentElement.classList.remove('overflow-hidden');
			window.removeEventListener('keydown', handleKeydown);
		}
	}

	const loadMore = (node): SvelteActionReturnType => {
		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					skip += size;
					triggerSearch();
				}
			});
		});

		observer.observe(node);

		return {
			destroy() {
				observer.unobserve(node);
				observer.disconnect();
			},
		};
	};

	let thumbs: HTMLElement;
	let viewportHeight: number;
	let scrollerHeight: number;

	const scroll = () => {
		const activeThumb = thumbs.children[index];

		if (activeThumb) {
			thumbs.scroll({ left: activeThumb.offsetLeft });
		}
	};

	$: if (thumbs && index + 1) {
		scroll();
	}

	const bindMousePosition = (node: HTMLElement) => {
		node.addEventListener('mousemove', (event: MouseEvent) => {
			node.style.transformOrigin = `${event.offsetX}px ${event.offsetY}px`;
		});
	};

	const showPicker = (event) => {
		try {
			event.target.showPicker();
		} catch (error) {
			console.log(error);
		}
	};
</script>

<svelte:window bind:innerHeight={viewportHeight} />

<Portal target="#header-meta">
	<div class="grid grid-cols-4 search" class:filters={filtersAreSet}>
		<button
			type="button"
			on:click={toggleFilter}
			aria-pressed={$filterIsVisible}
			class="inline-flex items-center"
		>
			<svg
				viewBox="0 0 28 28"
				width="28"
				height="28"
				aria-hidden="true"
				focusable="false"
				style="width: 0.7em; height: 0.7em;"
			>
				<line
					x1="0"
					y1="14"
					x2="28"
					y2="14"
					stroke="currentColor"
					stroke-width="1.5"
					vector-effect="non-scaling-stroke"
				/>
				{#if !$filterIsVisible}
					<line
						x1="14"
						y1="0"
						x2="14"
						y2="28"
						stroke="currentColor"
						stroke-width="1.5"
						vector-effect="non-scaling-stroke"
					/>
				{/if}
			</svg>
			<span class="ml-4">Filter</span>

			{#if filtersAreSet}
				&nbsp;<span>({filtersAreSet})</span>
			{/if}
		</button>
		<div class="whitespace-nowrap overflow-hidden col-span-2 leading-tight">
			{#if typeof total !== 'undefined'}
				<span>{total} <span class="hidden sm:inline">Treffer</span></span>
			{/if}
		</div>
		<div class="text-right hidden sm:inline">
			<input
				type="search"
				id=""
				placeholder="Suche"
				bind:value={$query}
				class="text-right px-1 w-full focus:outline-none placeholder:text-black focus:placeholder:text-gray-c bg-transparent"
			/>
		</div>
	</div>
</Portal>

{#if $filterIsVisible}
	<Portal target="#header-expand">
		<div class="advanced-search z-10">
			<Tabs name="filter" options={tabs} bind:activeTab />

			{#if activeTab.id === 'person'}
				{#if personOptions}
					{@const persons = Object.entries(personOptions).map(([person, _hits]) => {
						return {
							name: person,
							visible:
								Object.keys(personOptions).length < 30 ||
								getChar(person) === activePersonTab.id,
						};
					})}

					{#if persons.length >= 30}
						<div class="text-lg-fluid">
							<Tabs
								name="chars"
								options={personTabs}
								bind:activeTab={activePersonTab}
							/>
						</div>
					{/if}

					<fieldset class="pb-6">
						<legend class="sr-only">Person</legend>
						<div class="flex flex-wrap gutter-3 items-center">
							{#each persons as person (person)}
								<div class="checkbox" hidden={!person.visible}>
									<input
										type="checkbox"
										id="person-{person.name}"
										value={person.name}
										bind:group={$filteredPersons}
									/>
									<label class="text-lg-fluid" for="person-{person.name}"
										>{person.name}</label
									>
								</div>
							{/each}
						</div>
					</fieldset>
				{:else}
					<div>
						<button
							class="button flex items-center disabled:opacity-50"
							type="button"
							disabled={!filtersAreSet}
							on:click={reset}
							><svg
								width="14"
								height="12"
								viewBox="0 0 22.859 19"
								aria-hidden="true"
								class="mr-2"
								focusable="false"><use xlink:href="#icon-close" /></svg
							><span>Filter löschen</span></button
						>
					</div>
				{/if}
			{/if}
			{#if activeTab.id === 'datum'}
				{#if dateOptions}
					{@const dates = Object.keys(dateOptions)}

					{#if dates.length >= 20}
						<div class="text-lg-fluid">
							<Tabs name="date" options={dateTabs} bind:activeTab={activeDateTab} />
						</div>
						{#if activeDateTab.id === 'datum'}
							<fieldset class="pb-6">
								<legend class="sr-only">Datum</legend>
								<div class="flex gutter-3 items-center pt-[calc(1.3em+0.5rem)]">
									<div class="w-80">
										<label class="sr-only" for="exact-date">Datum</label>
										<input
											type="date"
											on:click={showPicker}
											required
											id="exact-date"
											class="text-lg-fluid"
											min={leftBoundary.toISOString().slice(0, 10)}
											max={$filteredTo ||
												rightBoundary.toISOString().slice(0, 10)}
											bind:value={$filteredFrom}
										/>
									</div>
								</div>
							</fieldset>
						{/if}
						{#if activeDateTab.id === 'zeitraum'}
							<fieldset class="pb-6">
								<legend class="sr-only">Zeitraum</legend>
								<div class="flex gutter-3 items-center">
									<div class="w-80">
										<label class="mb-2" for="daterange-from">Von</label>
										<input
											type="date"
											on:click={showPicker}
											required
											id="daterange-from"
											class="text-lg-fluid"
											min={leftBoundary.toISOString().slice(0, 10)}
											max={$filteredTo ||
												rightBoundary.toISOString().slice(0, 10)}
											bind:value={$filteredFrom}
										/>
									</div>
									<div class="w-80">
										<label class="mb-2" for="daterange-to">Bis</label>
										<input
											type="date"
											on:click={showPicker}
											required
											id="daterange-to"
											class="text-lg-fluid"
											min={$filteredFrom ||
												leftBoundary.toISOString().slice(0, 10)}
											max={rightBoundary.toISOString().slice(0, 10)}
											bind:value={$filteredTo}
										/>
									</div>
								</div>
							</fieldset>
						{/if}
					{:else}
						<fieldset class="pb-6">
							<legend class="sr-only">Datum</legend>
							<div class="flex flex-wrap gutter-3 items-center">
								{#each dates as date}
									<div class="checkbox">
										<input
											type="checkbox"
											id="date-{date}"
											value={date}
											bind:group={$filteredDates}
										/>
										<label class="text-lg-fluid" for="date-{date}"
											>{fmt.format(
												new Date(parseInt(date, 10) * 1000)
											)}</label
										>
									</div>
								{/each}
							</div>
						</fieldset>
					{/if}
				{:else}
					<div>
						<button
							class="button flex items-center disabled:opacity-50"
							type="button"
							disabled={!filtersAreSet}
							on:click={reset}
							><svg
								width="14"
								height="12"
								viewBox="0 0 22.859 19"
								aria-hidden="true"
								class="mr-2"
								focusable="false"><use xlink:href="#icon-close" /></svg
							><span>Filter löschen</span></button
						>
					</div>
				{/if}
			{/if}
			{#if activeTab.id === 'aufgabe'}
				<fieldset class="pb-6">
					<legend class="sr-only">Aufgabe</legend>
					<div class="flex flex-wrap gutter-3 items-center">
						{#each tasks as task, key}
							<div class="checkbox">
								<input
									name="filter-task"
									type="radio"
									id="task-{key}"
									value={task}
									bind:group={$filteredTask}
									on:click={() => {
										if (task === $filteredTask) {
											$filteredTask = '';
										}
									}}
								/>
								<label class="text-lg-fluid" for="task-{key}">{labels[task]}</label>
							</div>
						{/each}
					</div>
				</fieldset>
			{/if}
			<div
				class="flex sticky space-x-5 bottom-0 py-8 -mx-5 px-5 md:-mx-6 md:px-6 justify-center border-t bg-white bg-opacity-smidge backdrop-filter backdrop-blur-sm"
			>
				<button type="button" class="button flex items-center" on:click={toggleFilter}
					><svg
						width="14"
						height="12"
						viewBox="0 0 22.859 19"
						aria-hidden="true"
						class="mr-2"
						focusable="false"><use xlink:href="#icon-arrow" /></svg
					><span>Filter anwenden</span></button
				>
				<button
					class="button flex items-center disabled:opacity-50"
					type="button"
					disabled={!filtersAreSet}
					on:click={reset}
					><svg
						width="14"
						height="12"
						viewBox="0 0 22.859 19"
						aria-hidden="true"
						class="mr-2"
						focusable="false"><use xlink:href="#icon-close" /></svg
					><span>Filter löschen</span></button
				>
			</div>
		</div>
	</Portal>
{/if}

{#if objects.length}
	<div
		class="fixed bg-white bg-opacity-smidge backdrop-filter backdrop-blur-sm z-10 w-full"
		style="top: {$height}px"
		bind:clientHeight={scrollerHeight}
	>
		<div
			class="flex space-x-2 w-full overflow-auto -mx-4 md:-mx-6 px-4 md:px-6 py-6 border-b scroll-p-6 thumbs scroll-smooth"
			bind:this={thumbs}
		>
			{#each objects as object (object.id)}
				{#if object.images.length}
					<a
						href="{base}/object/{object.id}/"
						tabindex="-1"
						class="flex-shrink-0 overflow-hidden group relative"
					>
						<div
							class="group-hover:scale-150 transition-transform"
							use:bindMousePosition
						>
							<LazyImage image={object.images[0].preview} />
						</div>
					</a>
				{/if}
			{/each}
			{#if total > size + skip}
				<div class="flex" use:loadMore />
			{/if}
		</div>
	</div>

	<div style:margin-top="{scrollerHeight}px">
		<Scroller bind:index query="li" threshold={($height + scrollerHeight) / viewportHeight}>
			<div slot="foreground">
				<ObjectList {objects} />

				{#if total > size + skip}
					<div class="flex justify-center mt-4" use:loadMore>
						{#if pending}
							Lädt…
						{/if}
					</div>
				{/if}
			</div>
		</Scroller>
	</div>
{:else if searched && !pending}
	{#if $filteredTask === 'TRANSCRIBE'}
		<p class="py-6">
			Leider sind zurzeit schon alle Briefe transkribiert. Vielleicht werden Sie unter der
			Aufgabe <button
				type="button"
				class="underline"
				on:click={() => {
					$filteredTask = 'VERIFY';
				}}>Prüfen</button
			> noch fündig.
		</p>
	{:else if $filteredTask === 'VERIFY'}
		<p class="py-6">
			Leider sind zurzeit schon alle Briefe geprüft. Vielleicht werden Sie unter der Aufgabe <button
				type="button"
				class="underline"
				on:click={() => {
					$filteredTask = 'READ';
				}}>Lesen</button
			> noch fündig.
		</p>
	{:else}
		<p class="py-6">Keine Briefe gefunden.</p>
	{/if}

	{#if filtersAreSet}
		<button type="button" class="button flex items-center" on:click={reset}
			><svg
				width="14"
				height="12"
				viewBox="0 0 22.859 19"
				aria-hidden="true"
				class="mr-2"
				focusable="false"><use xlink:href="#icon-close" /></svg
			><span>Filter löschen</span></button
		>
	{/if}
{/if}

<style lang="postcss">
	.advanced-search {
		display: flex;
		flex-direction: column;
		block-size: 100%;
		inline-size: 100%;

		& > :last-child {
			margin-block-start: auto !important;
		}
	}

	.search {
		margin-inline: -0.5rem;

		& > * {
			padding-inline: 0.5rem;

			&:nth-child(1) {
				flex: 1;
			}
			&:nth-child(2) {
				flex: 3;
			}
		}

		&.filters {
			& > * {
				&:nth-child(1) {
					flex: 1;
				}
				&:nth-child(2) {
					flex: 2;
				}
				&:nth-child(3) {
					flex: 1;
				}
			}
		}
	}

	.thumbs {
		-ms-overflow-style: none;
		scrollbar-width: none;
		&::-webkit-scrollbar {
			display: none;
		}
	}
	input[type='date'] {
		background-color: transparent;
	}
	input[type='date']::-webkit-inner-spin-button,
	input[type='date']::-webkit-calendar-picker-indicator {
		display: none;
		-webkit-appearance: none;
	}

	input[type='search'] {
		text-decoration-thickness: 1px;
		text-underline-offset: 0.15em;

		&:not(:placeholder-shown) {
			text-decoration: underline;
		}

		&:hover {
			&::placeholder {
				text-decoration: underline;
			}
		}
		&::-webkit-search-cancel-button {
			display: none;
		}
	}
</style>
